// Remediation Tier 2 #11 — create / edit a CollectionTemplateBundle. Slots
// are an ordered list of (templateId, isRequired, dueOffsetDays). The
// template picker respects the bundle's audience filter so Buyer bundles
// don't accidentally pull Broker-only templates.

import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, Save, Trash2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  Input,
  Textarea,
} from "@/design-system/components/ui";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/components/ui/Toast";
import {
  collectionBundlesApi,
  type CollectionTemplateBundleDto,
  type SaveCollectionTemplateBundleSlotPayload,
} from "@/api/collectionBundles";
import {
  COLLECTION_AUDIENCES,
  collectionAudienceLabel,
  collectionTemplatesApi,
  formatAudiences,
  type CollectionTemplateDto,
  type MaterialAudience,
} from "@/api/collectionTemplates";
import { TemplateKindBadge } from "@/components/Collection/TemplateKindBadge";

interface SlotState {
  templateId: string;
  isRequired: boolean;
  dueOffsetDays: string; // text-bound so "" represents null
}

interface FormState {
  name: string;
  description: string;
  audience: MaterialAudience | "";
  audienceTag: string;
  active: boolean;
  slots: SlotState[];
}

const DEFAULT_FORM: FormState = {
  name: "",
  description: "",
  audience: "",
  audienceTag: "",
  active: true,
  slots: [],
};

function fromDto(b: CollectionTemplateBundleDto): FormState {
  return {
    name: b.name,
    description: b.description ?? "",
    audience: b.audience ?? "",
    audienceTag: b.audienceTag ?? "",
    active: b.active,
    slots: b.slots
      .slice()
      .sort((a, c) => a.order - c.order)
      .map((s) => ({
        templateId: s.templateId,
        isRequired: s.isRequired,
        dueOffsetDays:
          s.dueOffsetDays == null ? "" : String(s.dueOffsetDays),
      })),
  };
}

function toSlotPayload(slots: SlotState[]): SaveCollectionTemplateBundleSlotPayload[] {
  return slots.map((s, i) => {
    const offsetTrim = s.dueOffsetDays.trim();
    const offset = offsetTrim === "" ? null : Number.parseInt(offsetTrim, 10);
    return {
      templateId: s.templateId,
      order: i + 1,
      isRequired: s.isRequired,
      dueOffsetDays: offset != null && Number.isFinite(offset) ? offset : null,
    };
  });
}

/** A bundle save / deactivate must reach the admin grid AND the composer's
 *  ["collection-bundles","active",partyRole] picker (inactive here — only
 *  stale-marked). Shared with the list page's deactivate; exported for the
 *  invalidation-harness unit test. */
export function invalidateCollectionBundleReads(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ["collection-bundles-admin"] });
  void qc.invalidateQueries({ queryKey: ["collection-bundles"] });
}

export function CollectionTemplateBundleEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<FormState>(DEFAULT_FORM);

  const existing = useQuery({
    queryKey: ["collection-bundle", id],
    queryFn: () => collectionBundlesApi.get(id!),
    enabled: !isNew,
  });

  useEffect(() => {
    if (existing.data) setState(fromDto(existing.data));
  }, [existing.data]);

  // Template picker — respects the bundle's audience filter (Part A reuse).
  // When the bundle has no audience set we show "All audiences".
  const templatesQuery = useQuery({
    queryKey: ["collection-templates-for-bundle", state.audience || null],
    queryFn: () =>
      collectionTemplatesApi.list(false, state.audience || null),
  });
  const templates: CollectionTemplateDto[] = templatesQuery.data ?? [];
  const templatesById = useMemo(
    () => new Map(templates.map((t) => [t.id, t] as const)),
    [templates],
  );

  const [templateToAdd, setTemplateToAdd] = useState("");

  const create = useMutation({
    mutationFn: () =>
      collectionBundlesApi.create({
        name: state.name.trim(),
        description: state.description.trim() || null,
        audience: (state.audience || null) as MaterialAudience | null,
        audienceTag: state.audienceTag.trim() || null,
        slots: toSlotPayload(state.slots),
      }),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Bundle '${dto.name}' created.` });
      void qc.invalidateQueries({ queryKey: ["collection-bundles-admin"] });
      navigate(`/admin/collection-bundles/${dto.id}`);
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const update = useMutation({
    mutationFn: () =>
      collectionBundlesApi.update(id!, {
        name: state.name.trim(),
        description: state.description.trim() || null,
        audience: (state.audience || null) as MaterialAudience | null,
        audienceTag: state.audienceTag.trim() || null,
        active: state.active,
        slots: toSlotPayload(state.slots),
      }),
    onSuccess: (dto) => {
      toast({ tone: "success", message: `Bundle '${dto.name}' saved.` });
      invalidateCollectionBundleReads(qc);
      void qc.invalidateQueries({ queryKey: ["collection-bundle", id] });
    },
    onError: (e: Error) => toast({ tone: "error", message: e.message }),
  });

  const validation = validate(state);
  const canSave =
    validation.length === 0 && !create.isPending && !update.isPending;

  function patch(p: Partial<FormState>) {
    setState((s) => ({ ...s, ...p }));
  }

  function addSlot() {
    if (!templateToAdd) return;
    if (state.slots.some((s) => s.templateId === templateToAdd)) return;
    setState((s) => ({
      ...s,
      slots: [
        ...s.slots,
        { templateId: templateToAdd, isRequired: true, dueOffsetDays: "" },
      ],
    }));
    setTemplateToAdd("");
  }

  function move(index: number, delta: number) {
    setState((s) => {
      const next = s.slots.slice();
      const target = index + delta;
      if (target < 0 || target >= next.length) return s;
      [next[index], next[target]] = [next[target], next[index]];
      return { ...s, slots: next };
    });
  }

  function removeSlot(index: number) {
    setState((s) => ({
      ...s,
      slots: s.slots.filter((_, i) => i !== index),
    }));
  }

  return (
    <div className="flex flex-col gap-space-4">
      <PageHeader
        eyebrow="Collection · Bundle"
        title={isNew ? "New bundle" : (existing.data?.name ?? "Edit bundle")}
        description="Reusable set of templates. The composer's 'Add from group' picker drops every slot into a new package in slot order."
        actions={
          <div className="flex items-center gap-space-2">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate("/admin/collection-bundles")}
            >
              <ArrowLeft size={14} className="mr-space-1" /> Back
            </Button>
            <Button
              type="button"
              disabled={!canSave}
              onClick={() => (isNew ? create.mutate() : update.mutate())}
            >
              <Save size={14} className="mr-space-1" />
              {isNew ? "Create bundle" : "Save bundle"}
            </Button>
          </div>
        }
      />

      {validation.length > 0 && (
        <div className="rounded-md bg-status-error/10 p-space-3 text-small text-onyx">
          <ul className="m-0 pl-space-4">
            {validation.map((v, i) => (
              <li key={i}>{v}</li>
            ))}
          </ul>
        </div>
      )}

      <Card>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-space-3 p-space-4">
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>
              Name <span className="text-error">*</span>
            </span>
            <Input
              value={state.name}
              onChange={(e) => patch({ name: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Audience</span>
            <Select
              value={state.audience}
              onChange={(e) =>
                patch({ audience: e.target.value as MaterialAudience | "" })
              }
            >
              <option value="">No specific audience</option>
              {COLLECTION_AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {collectionAudienceLabel(a)}
                </option>
              ))}
            </Select>
            <span className="text-caption text-gray-mid">
              Filters the template picker below and the composer's "Add from group" list.
            </span>
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx md:col-span-2">
            <span>Description</span>
            <Textarea
              rows={2}
              value={state.description}
              onChange={(e) => patch({ description: e.target.value })}
            />
          </label>
          <label className="flex flex-col gap-space-1 text-small text-onyx">
            <span>Audience tag (legacy display label)</span>
            <Input
              value={state.audienceTag}
              onChange={(e) => patch({ audienceTag: e.target.value })}
              placeholder="optional"
            />
            <span className="text-caption text-gray-mid">
              Free-text label kept for the seeded broker-application bundle; the structured
              Audience above drives filtering.
            </span>
          </label>
          {!isNew && (
            <label className="flex items-center gap-space-2 text-small text-onyx">
              <input
                type="checkbox"
                checked={state.active}
                onChange={(e) => patch({ active: e.target.checked })}
              />
              Active (uncheck to hide from the composer picker)
            </label>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-space-3 p-space-4">
          <h2 className="text-h5 font-display text-onyx m-0">Slots</h2>
          <p className="text-small text-gray-mid m-0">
            Ordered list of templates that get instantiated, in this order, when the
            composer's "Add from group" is used.
          </p>

          <div className="flex gap-space-2">
            <Select
              value={templateToAdd}
              onChange={(e) => setTemplateToAdd(e.target.value)}
              disabled={templatesQuery.isLoading}
            >
              <option value="">
                {templatesQuery.isLoading
                  ? "Loading templates…"
                  : "Select a template…"}
              </option>
              {templates
                .filter(
                  (t) =>
                    t.active &&
                    !state.slots.some((s) => s.templateId === t.id),
                )
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} · {t.kind} · {formatAudiences(t.audiences).text}
                  </option>
                ))}
            </Select>
            <Button
              type="button"
              variant="secondary"
              onClick={addSlot}
              disabled={!templateToAdd}
            >
              Add slot
            </Button>
          </div>

          {state.slots.length === 0 ? (
            <p className="text-caption text-gray-mid italic">
              No slots yet. Add at least one before saving.
            </p>
          ) : (
            <ul className="divide-y divide-border-light border border-border-light rounded-md m-0 p-0 list-none">
              {state.slots.map((slot, i) => {
                const t = templatesById.get(slot.templateId);
                return (
                  <li
                    key={slot.templateId}
                    className="flex items-center justify-between gap-space-3 p-space-3"
                  >
                    <div className="flex items-center gap-space-2 min-w-0">
                      <span className="text-caption text-gray-mid w-space-5 text-right">
                        #{i + 1}
                      </span>
                      <TemplateKindBadge
                        kind={t?.kind ?? "—"}
                        isSpecialForm={t?.isSpecialForm}
                      />
                      <span className="text-small text-onyx truncate">
                        {t?.name ?? slot.templateId}
                      </span>
                    </div>
                    <div className="flex items-center gap-space-2">
                      <label className="flex items-center gap-space-1 text-caption text-onyx">
                        <input
                          type="checkbox"
                          checked={slot.isRequired}
                          onChange={(e) =>
                            setState((s) => {
                              const next = s.slots.slice();
                              next[i] = { ...next[i], isRequired: e.target.checked };
                              return { ...s, slots: next };
                            })
                          }
                        />
                        Required
                      </label>
                      <label className="flex items-center gap-space-1 text-caption text-onyx">
                        <span>Due offset (days)</span>
                        <Input
                          type="number"
                          value={slot.dueOffsetDays}
                          onChange={(e) =>
                            setState((s) => {
                              const next = s.slots.slice();
                              next[i] = {
                                ...next[i],
                                dueOffsetDays: e.target.value,
                              };
                              return { ...s, slots: next };
                            })
                          }
                          className="w-20"
                        />
                      </label>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={() => move(i, -1)}
                        disabled={i === 0}
                        aria-label="Move up"
                      >
                        <ArrowUp size={14} aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={() => move(i, 1)}
                        disabled={i === state.slots.length - 1}
                        aria-label="Move down"
                      >
                        <ArrowDown size={14} aria-hidden />
                      </Button>
                      <Button
                        type="button"
                        variant="tertiary"
                        size="sm"
                        onClick={() => removeSlot(i)}
                        aria-label="Remove slot"
                      >
                        <Trash2 size={14} aria-hidden />
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function validate(state: FormState): string[] {
  const errors: string[] = [];
  if (!state.name.trim()) errors.push("Name is required.");
  if (state.slots.length === 0) errors.push("Add at least one slot.");
  for (const s of state.slots) {
    if (s.dueOffsetDays.trim() !== "") {
      const n = Number.parseInt(s.dueOffsetDays, 10);
      if (!Number.isFinite(n) || n < 0) {
        errors.push("Due offset days must be a non-negative number.");
        break;
      }
    }
  }
  return errors;
}
